import { ChangeDetectorRef, Component, EventEmitter, OnDestroy, OnInit, Output } from "@angular/core";
import { platform } from "../../../../../platform/platform";
import { IMAGE_FILETYPE, PLATFORM } from "../../../../app.constants";
import { NetworkStatusService } from "../../../../_services/network-status.service";
import { IplexDeviceConnectModeService } from "../../../../_offline/services/iplex-device-connect-mode.service";
import { IplexDeviceSyncService } from "../../../../_offline/services/iplex-device-sync.service";
import { FileStat } from "webdav";
import { take, takeUntil } from "rxjs/operators";
import { Subject, zip } from "rxjs";
import { SpinnerService } from "../../../../_services/spinner.service";
import { CommonSnackBarService } from "../../../../_services/common-snack-bar.service";
import { MatTreeNestedDataSource } from "@angular/material/tree";
import { NestedTreeControl } from "@angular/cdk/tree";
import { FormatDate } from "../../../../_objects/format-date";
import { TranslateService } from "@ngx-translate/core";

export class DeviceFile {
  id: string;
  file: File;
  stat: FileStat;

  constructor(id: string, file: File, stat: FileStat) {
    this.id = id;
    this.file = file;
    this.stat = stat;
  }
}

export class ImportIplexDeviceImageWrapper {
  imageFiles: FileWrapper[];
  isDeletedWithUploading: boolean;
}

export class FileWrapper {
  fileStat: FileStat;
  bookMarkStat: FileStat;
  audioStat: FileStat;
  isSelected: boolean;
  baseFolderPath: string;
  baseFolderStat: FileStat;
  parentFolderStat: FileStat;

  constructor(
    file: FileStat,
    selected: boolean,
    baseFolderStat: FileStat,
    parentFolderStat: FileStat,
    bookMarkStat: FileStat = null,
    audioStat: FileStat = null
  ) {
    this.fileStat = file;
    this.isSelected = selected;
    this.baseFolderStat = baseFolderStat;
    this.parentFolderStat = parentFolderStat;
    this.bookMarkStat = bookMarkStat;
    this.audioStat = audioStat;
  }

  get displayName() {
    return this.fileStat.filename.replace(this.parentFolderStat?.filename, "");
  }

  get displayNameWithFolder() {
    return this.fileStat.filename.replace(this.baseFolderStat?.filename, "");
  }

  get date() {
    const date = new FormatDate(this.fileStat.lastmod);
    return date.toString();
  }
}

export class FolderWrapper {
  fileStat: FileStat;
  folderChildren: FolderWrapper[];
  isParent: boolean;
  parentFolder: FileStat;
  isSelected: boolean;

  get isSelectedAllChild() {
    if (!this.isParent) {
      return false;
    }

    let isSelectedAllChild = true;
    if (this.folderChildren && this.folderChildren.length > 0) {
      isSelectedAllChild = this.folderChildren.every((child) => child.isSelected);
    }

    return this.isParent && isSelectedAllChild;
  }

  get isUnSelectedAllChild(): boolean {
    if (!this.isParent) {
      return false;
    }

    let isUnselectedAllChild = true;
    if (this.folderChildren && this.folderChildren.length > 0) {
      isUnselectedAllChild = this.folderChildren.every((_) => !_.isSelected);
    }

    return this.isParent && isUnselectedAllChild;
  }

  get isIndeterminateAllFiles(): boolean {
    let match = false,
      unMatch = false;
    if (this.folderChildren && this.folderChildren.length > 0) {
      return this.folderChildren.some((folder) => {
        if (folder.isSelected) {
          match = true;
        } else {
          unMatch = true;
        }
        return match && unMatch;
      });
    }
    return !this.isSelectedAllChild && !this.isUnSelectedAllChild;
  }

  constructor(file: FileStat, parentFolder: FileStat, children: FolderWrapper[] = [], isSelected = false) {
    this.fileStat = file;
    this.isParent = parentFolder === null;
    this.parentFolder = parentFolder;
    this.folderChildren = children;
    this.isSelected = isSelected;
  }

  get hasChildren() {
    return this.folderChildren?.length > 0;
  }
}

@Component({
  selector: "app-save-iplex-device-images",
  templateUrl: "./save-iplex-device-images.component.html",
  styleUrls: ["./save-iplex-device-images.component.scss"],
  standalone: false
})
export class SaveIplexDeviceImagesComponent implements OnInit, OnDestroy {
  @Output() public importIplexDeviceImages = new EventEmitter<ImportIplexDeviceImageWrapper>();
  @Output() public closeEmitter = new EventEmitter<boolean>();
  imageFiles: FileWrapper[] = [];
  isDeletedWithUploading = false;
  selectedFolder: FileStat;

  nestedTreeControl: NestedTreeControl<FolderWrapper>;
  nestedDataSource: MatTreeNestedDataSource<FolderWrapper>;
  private onDestroy$ = new Subject();
  readonly displayedColumns: string[] = ["checkbox", "filename", "date", "size"];
  readonly dateFormat = "yyyy/MM/dd H:mm";

  get isDesktop() {
    return this.networkStatusService.isDesktop();
  }

  get isOffline() {
    return this.networkStatusService.isOffline;
  }

  get isDeviceConnectMode() {
    return this.isOffline && this.iplexDeviceConnectModeService.isHttpsConnectMode;
  }

  get selectedImageCount() {
    if (!this.imageFiles) {
      return 0;
    }

    return this.imageFiles.filter((_) => _.isSelected && this.isImageFile(_.fileStat.mime, _.fileStat.size, _.fileStat.basename)).length;
  }

  get selectedVideoCount() {
    if (!this.imageFiles) {
      return 0;
    }
    return this.imageFiles.filter((_) => _.isSelected && this.isVideoFile(_.fileStat.size, _.fileStat.basename)).length;
  }
  get selectedTotalFileSize() {
    let result = 0;
    if (!this.imageFiles) {
      return result;
    }

    const fileSizeList = this.imageFiles.filter((_) => _.isSelected).map((_) => _.fileStat.size);

    fileSizeList.forEach((_) => (result += _));
    return result;
  }

  constructor(
    private networkStatusService: NetworkStatusService,
    private iplexDeviceConnectModeService: IplexDeviceConnectModeService,
    private iplexDeviceSyncService: IplexDeviceSyncService,
    private snackBar: CommonSnackBarService,
    private oscSpinnerService: SpinnerService,
    private cd: ChangeDetectorRef,
    private translate: TranslateService
  ) {}

  ngOnInit(): void {
    this.imageFiles = [];
    this.nestedDataSource = new MatTreeNestedDataSource();
    this.nestedTreeControl = new NestedTreeControl<FolderWrapper>(this.getChildren);

    this.iplexDeviceConnectModeService.isHttpsModeObserver().subscribe((isHttpsMode) => {
      if (!isHttpsMode) {
        this.closeEmitter.emit(true);
      }
    });

    this.iplexDeviceSyncService
      .onWebDAVConnectMode()
      .pipe(takeUntil(this.onDestroy$))
      .subscribe(
        (isSuccess) => {
          if (!isSuccess) {
            this.snackBar.open(this.translate.instant("iplexDeviceConnectModeService.failedConnect"));
            this.oscSpinnerService.hide(true);
            return;
          }

          this.getFirstFolders();
        },
        (error) => {
          console.log(error);
          this.snackBar.open(this.translate.instant("iplexDeviceConnectModeService.failedConnect"));
          this.oscSpinnerService.hide(true);
        }
      );
  }

  ngOnDestroy(): void {
    this.onDestroy$.next(undefined);
    this.onDestroy$.unsubscribe();

    this.iplexDeviceSyncService
      .offWebDAVConnectMode()
      .pipe(take(1))
      .subscribe((_) => _);
  }

  public hasNestedChild(_: number, folder: FolderWrapper) {
    return folder?.isParent;
  }

  private getChildren = (node: FolderWrapper) => node.folderChildren;

  getFirstFolders() {
    this.oscSpinnerService.show(true);
    this.iplexDeviceSyncService
      .getDirectoriesInSDcard("/")
      .pipe(takeUntil(this.onDestroy$))
      .subscribe(
        (folders) => {
          const folderWrappers = folders.map((folder) => new FolderWrapper(folder, null));
          this.updateDataSource(folderWrappers, null);
          this.oscSpinnerService.hide(true);
        },
        (error) => {
          console.log(error);
          this.snackBar.open(this.translate.instant("saveIplexDeviceImagesComponent.errorGetData"));
          this.oscSpinnerService.hide(true);
        }
      );
  }

  selectFolder(folder: FolderWrapper) {
    if (!folder) {
      return;
    }

    this.selectedFolder = folder.fileStat;
    this.oscSpinnerService.show(true);
    zip(
      this.iplexDeviceSyncService.getALLContentsFromInSDcard(folder.fileStat.filename),
      this.iplexDeviceSyncService.getDirectoriesInSDcard(folder.fileStat.filename)
    )
      .pipe(takeUntil(this.onDestroy$))
      .subscribe(
        (values) => {
          this.imageFiles = [];
          const allContents = values[0];
          const directories = values[1];
          if (folder.isParent && directories?.length > 0) {
            const data = this.nestedDataSource.data;
            // 親フォルダーの場合は、子フォルダーを追加
            data.some((d) => {
              if (d?.fileStat.basename == folder.fileStat.basename) {
                const folders = directories.filter((_) => _.type === "directory").map((_) => new FolderWrapper(_, d.fileStat));
                d.folderChildren = folders;
                return true;
              }
            });

            this.updateDataSource(data, folder);
          }

          // ファイルを一覧表示
          const files = allContents.filter((file) => file.type === "file").filter((file) => this.isReceivedFile(file.mime, file.size, file.basename));

          if (files && files.length > 0) {
            const bookMarkFiles = allContents
              .filter((file) => file.type === "file")
              .filter((file) => file.basename && file.basename.toLowerCase().split(".").pop().match("json|jsn"));

            const audioFiles = allContents
              .filter((file) => file.type === "file")
              .filter((file) => file.basename && file.basename.toLowerCase().split(".").pop().match("wav"));

            this.imageFiles = files.map((file) => {
              const withOutMime = this.getWithoutFileName(file.basename);
              const bookMarkFile = bookMarkFiles.find((bm) => this.getWithoutFileName(bm.basename) === withOutMime);
              const audioFile = audioFiles.find((audio) => this.getWithoutFileName(audio.basename) === withOutMime);

              return new FileWrapper(file, true, folder.fileStat, folder.fileStat, bookMarkFile, audioFile);
            });
          }
          this.oscSpinnerService.hide(false);
        },
        (error) => {
          console.log(error);
          this.oscSpinnerService.hide(false);
        }
      );
  }

  selectAll() {
    if (this.isOffline) {
      const isAllSelected = this.isSelectedImageAll();
      this.imageFiles.forEach((imageFile) => (imageFile.isSelected = !isAllSelected));
    }
  }

  isSelectedImageAll(): boolean {
    return this.imageFiles.every((imageFile) => imageFile.isSelected);
  }

  isUnSelectedImageAll(): boolean {
    return this.imageFiles.every((imageFile) => !imageFile.isSelected);
  }

  isIndeterminateImageAll(): boolean {
    let match = false,
      unMatch = false;
    if (this.imageFiles && this.imageFiles.length > 0) {
      this.imageFiles.forEach((file) => {
        if (file.isSelected) {
          match = true;
        } else {
          unMatch = true;
        }
      });
    }
    return match && unMatch;
  }

  receiveFiles() {
    const result = new ImportIplexDeviceImageWrapper();
    result.imageFiles = this.imageFiles.filter((_) => _.isSelected);
    result.isDeletedWithUploading = this.isDeletedWithUploading;

    this.importIplexDeviceImages.emit(result);
  }

  updateDataSource(folders: FolderWrapper[], expandNode: FolderWrapper) {
    this.nestedDataSource.data = [];
    this.nestedDataSource.data = folders;

    const target = folders.find((folder) => folder?.fileStat.basename === expandNode?.fileStat.basename);
    if (target) {
      this.nestedTreeControl.expand(target);
    }

    this.cd.detectChanges();
  }

  private isReceivedFile(filetype: string, fileSize: number, filename: string): boolean {
    return this.isImageFile(filetype, fileSize, filename) || this.isVideoFile(fileSize, filename);
  }

  getWithoutFileName(fileName: string) {
    const fileNameArray = fileName.toLowerCase().split(".");
    fileNameArray.pop();
    return fileNameArray.join(".");
  }

  private isImageFile(filetype: string, fileSize: number, filename: string) {
    return (
      (filetype.match(IMAGE_FILETYPE.JPEG) ||
        filetype.match(IMAGE_FILETYPE.PNG) ||
        filetype.match(IMAGE_FILETYPE.BMP) ||
        filetype.match(IMAGE_FILETYPE.TIF) ||
        filetype.match(IMAGE_FILETYPE.TIFF) ||
        filename?.toLowerCase().split(".").pop().match("heic|heif")) &&
      fileSize > 0
    );
  }

  private isVideoFile(fileSize: number, filename: string) {
    if (fileSize <= 0) return false;
    const extension = filename.toLowerCase().split(".").pop();
    const validExtensions = ["mp4", "avi"];
    return extension ? validExtensions.includes(extension) : false;
  }
}
